import { type MutableRefObject, useEffect } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/**
 * Clears the shape selection on entering text creation mode, unless a shape has just entered text
 * edit as part of the same transition.
 */
export function useTextCreationSelectionReset({
  internalShapeManagerRef,
  isInTextCreationMode,
  selectedTextBoxIdsRef,
  clearShapeSelectionWithHistory,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isInTextCreationMode: boolean
  selectedTextBoxIdsRef: MutableRefObject<string[]>
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
}) {
  // When entering text creation mode, clear shape selection — but not out from under a shape
  // that just entered text edit as part of this same transition (e.g. `handleActivateTool`'s
  // "auto-enter edit for the sole selected, eligible shape" shortcut, and its `onActivateTextTool`
  // T-key twin). Both run synchronously on the click, before `activeItem`/`isInTextCreationMode`
  // update, so without this guard this effect fires a tick later and wipes the selection (and,
  // functionally, the just-started edit session) right back out — the shape never visibly shows
  // as selected or editing, even though the shortcut did enter it correctly for a moment.
  useEffect(() => {
    if (!isInTextCreationMode) return
    if (internalShapeManagerRef.current?.getEditingShapeTextId()) return
    clearShapeSelectionWithHistory(selectedTextBoxIdsRef.current)
  }, [
    clearShapeSelectionWithHistory,
    isInTextCreationMode,
    internalShapeManagerRef,
    selectedTextBoxIdsRef,
  ])
}
